import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Shield,
  Users,
  FileCheck,
  GraduationCap,
  Handshake,
  AlertTriangle,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  Heart,
  Eye,
  Lock,
} from "lucide-react";

const safeguardingPillars = [
  {
    icon: Users,
    title: "Safer Recruitment",
    description:
      "All staff undergo rigorous recruitment processes including enhanced DBS checks, reference verification, and comprehensive interview assessments.",
  },
  {
    icon: FileCheck,
    title: "DBS Checks",
    description:
      "Enhanced DBS checks with barred list checks are completed for all staff before they begin work, with regular updates as required.",
  },
  {
    icon: GraduationCap,
    title: "Training & Development",
    description:
      "Comprehensive safeguarding training for all staff, with regular updates and specialist training in areas such as exploitation and mental health.",
  },
  {
    icon: Eye,
    title: "Supervision & Support",
    description:
      "Regular supervision sessions for all staff to discuss safeguarding concerns, review practice, and ensure wellbeing.",
  },
  {
    icon: Handshake,
    title: "Partnership Working",
    description:
      "Strong partnerships with local authorities, police, health services and other agencies to ensure coordinated safeguarding responses.",
  },
  {
    icon: MessageSquare,
    title: "Whistleblowing",
    description:
      "Clear whistleblowing procedures that empower staff to raise concerns confidentially and without fear of reprisal.",
  },
];

const commitments = [
  "Prioritising the safety and wellbeing of young people above all else",
  "Creating environments where young people feel safe and supported",
  "Responding promptly and appropriately to all safeguarding concerns",
  "Working collaboratively with external agencies and professionals",
  "Maintaining robust record-keeping and information sharing protocols",
  "Regularly reviewing and updating our safeguarding policies and procedures",
];

export default function SafeguardingPage() {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-slate-900 text-white">
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Safeguarding
              </div>
              <h1
                className="text-4xl sm:text-5xl font-extrabold leading-tight mb-6"
                data-testid="safeguarding-headline"
              >
                Safeguarding is at the{" "}
                <span className="text-teal-400">heart of everything we do</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed">
                Epic Care is committed to safeguarding and promoting the welfare
                of all young people in our care. Our robust policies, procedures
                and practices ensure that protection is embedded throughout our
                organisation.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section className="section-padding bg-white" data-testid="commitment-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-teal-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Our Safeguarding Commitment
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                At Epic Care, we believe that every young person has the right
                to be safe, respected and supported. Our safeguarding commitment
                extends to all aspects of our work and is underpinned by our
                values of empowerment and protection.
              </p>
              <div className="space-y-4">
                {commitments.map((commitment, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{commitment}</span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="bg-slate-900 rounded-3xl p-8 md:p-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-teal-500/20 rounded-2xl flex items-center justify-center">
                    <Lock className="w-8 h-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">
                      Protection is Central
                    </h3>
                    <p className="text-teal-300">EPIC CARE ethos</p>
                  </div>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  "At Epic Care, Empowerment and Protection are central to
                  everything we do. We are committed to creating safe spaces
                  where young people can heal, grow and thrive."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safeguarding Pillars */}
      <section className="section-padding bg-slate-50" data-testid="pillars-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Safeguarding Framework
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive measures to ensure the safety and wellbeing of all
              young people
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {safeguardingPillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                  <pillar.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-3">
                  {pillar.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Concerns & Complaints */}
      <section className="section-padding bg-white" data-testid="concerns-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Raising Concerns
              </h2>
              <p className="text-lg text-slate-600">
                We take all safeguarding concerns seriously and have clear
                processes in place
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-amber-900 text-lg">
                    Safeguarding Concerns
                  </h3>
                </div>
                <p className="text-amber-800 text-sm leading-relaxed mb-4">
                  If you have concerns about the safety or welfare of a young
                  person in our care, please contact us immediately. All
                  concerns will be taken seriously and responded to promptly.
                </p>
                <a
                  href="mailto:info@epiccareltd.co.uk"
                  className="text-amber-700 font-medium hover:text-amber-800"
                  data-testid="safeguarding-email"
                >
                  info@epiccareltd.co.uk
                </a>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-lg">
                    Complaints Process
                  </h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">
                  We have a clear complaints procedure in place. If you wish to
                  make a complaint about our services, please contact us and we
                  will respond in line with our complaints policy.
                </p>
                <Link
                  to="/contact"
                  className="text-teal-600 font-medium hover:text-teal-700"
                  data-testid="complaints-link"
                >
                  Contact Us →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-slate-800 to-slate-900" data-testid="safeguarding-cta-section">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Questions about our safeguarding approach?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto">
            We're happy to discuss our safeguarding policies and procedures in
            more detail.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-teal-500 hover:bg-teal-600 text-white rounded-full px-8"
                data-testid="safeguarding-contact-btn"
              >
                Contact Us
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/referrals">
              <Button
                size="lg"
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 rounded-full px-8"
                data-testid="safeguarding-referral-btn"
              >
                Make a Referral
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
